import type { ActivityItem, ActivityStatus } from '../ActivityThread/activity-thread-types'
import type { AgentMessageStep } from '@/types/chat'

/** Projects persisted tool steps onto activity items, marking the first unfinished step as running and a step that ended badly as failed. */
export function toActivityItems(steps: readonly AgentMessageStep[]): ActivityItem[] {
  const activeIndex = steps.findIndex((step) => !step.completedAt)

  return steps.map((step, index) => {
    let status: ActivityStatus
    if (step.completedAt) {
      status = step.ok ? 'complete' : 'failed'
    } else if (index === activeIndex) {
      status = 'active'
    } else {
      status = 'pending'
    }

    const item: ActivityItem = {
      id: step.id,
      kind: step.kind,
      labelKey: step.labelKey,
      ...(step.labelParams ? { labelParams: step.labelParams } : {}),
      ...(step.sources?.length ? { sources: step.sources } : {}),
      status,
    }

    return item
  })
}

/** Elapsed time of a finished run, first `startedAt` to last `completedAt`; `undefined` when empty or still running. */
export function activityDuration(steps: readonly AgentMessageStep[]): number | undefined {
  if (steps.length === 0) return undefined
  const last = steps[steps.length - 1]
  if (!last?.completedAt) return undefined
  const first = steps[0]
  if (!first) return undefined

  const start = Date.parse(first.startedAt)
  const end = Date.parse(last.completedAt)
  if (Number.isNaN(start) || Number.isNaN(end)) return undefined

  return Math.max(0, Math.round((end - start) / 1000))
}
